<i18n>
{
  "no": {
    "errors": {
      "expired": "Økten har utløpt. Lukk nettleser og forsøk igjen.",
      "badOtp": "Bekreftelseskode du har tastet inn er feil"
    },
    "verify": "Bekreft eierskap",
    "smsSentTo": "Vi sendte en bekreftelseskode til",
    "andOthers": "og {0} andre",
    "showLess": "Vis mindre",
    "oneTimeCode": "bekreftelseskode",
    "resendIn": "Du kan få tilsendt en ny kode om {0} sekund",
    "didNotGetCode": "Ikke mottatt koden?",
    "resend": "Send på nytt",
    "submit": "Bekreft"
  },
  "en": {
    "errors": {
      "expired": "This session has expired. Please close this browser window and try again.",
      "badOtp": "The verification code you entered was incorrect."
    },
    "verify": "Verify ownership",
    "smsSentTo": "We sent a verification code to",
    "andOthers": "and {0} other{1}",
    "showLess": "Show less",
    "oneTimeCode": "Code",
    "resendIn": "You can have a new code sent in {0} second(s)",
    "didNotGetCode": "Did not get the code?",
    "resend": "Resend",
    "submit": "Confirm"
  }
}
</i18n>

<template>
  <FormWrapper>
    <div class="page-md">
      <TnHeading type="h1" size="s" align="center" class="margin-top-2xl">
        {{ t('smsSentTo') }}
      </TnHeading>

      <ul class="margin-bottom-l text-center">
        <li v-for="number in visibleNumbers" :key="number">
          <TnHeading type="h2" size="2xs" align="center">{{ number }}</TnHeading>
        </li>
        <li v-if="hiddenNumbers.length" class="margin-top-m">
          <TnLink
            size="s"
            :text="numbersListText"
            @click="numbersDropdownOpen = !numbersDropdownOpen"
          />
        </li>
      </ul>

      <TnNotificationInline
        v-if="error"
        horizontal
        status="critical"
        full-width
        class="margin-bottom-l"
      >
        {{ t(error) }}
      </TnNotificationInline>

      <form ref="formEl" @submit.prevent="onSubmit">
        <VerificationCodeForm v-model="code" />
      </form>

      <div class="margin-top-l text-center">
        <TnParagraph v-if="resendTimer > 0" size="s">
          {{ t('resendIn', [resendTimer]) }}
        </TnParagraph>
        <TnParagraph v-else size="s">
          {{ t('didNotGetCode') }}
          <TnLink size="s" :text="t('resend')" @click="resend" />
        </TnParagraph>
      </div>
    </div>
  </FormWrapper>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch, useTemplateRef } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import {
  TnHeading,
  TnLink,
  TnNotificationInline,
  TnParagraph,
} from '@telenornorgeinternal/telegrafen-vue3-core';

import FormWrapper from '@/components/FormWrapper.vue';
import VerificationCodeForm from '@/views/ibis/sign-up/VerificationCodeForm.vue';
import { VerificationService } from '@/views/ibis/sign-up/verification.service';
import type { ApiError } from '@/types';

const { t } = useI18n();
const route = useRoute();

const verificationService = new VerificationService();
const verificationId = route.query.codeId as string;

const formEl = useTemplateRef<HTMLFormElement>('formEl');
const error = ref<string | null>(null);
const code = ref('');
const sentTo = ref('');
const resendTimer = ref(2);
const numbersDropdownOpen = ref(false);

// The Vue 2 version mutated component state from inside a computed (and
// spliced the array in place); these are now pure derivations.
const allNumbers = computed(() => (sentTo.value ? sentTo.value.split(',') : []));
const visibleNumbers = computed(() =>
  numbersDropdownOpen.value ? allNumbers.value : allNumbers.value.slice(0, 3),
);
const hiddenNumbers = computed(() => allNumbers.value.slice(3));

const numbersListText = computed(() => {
  if (numbersDropdownOpen.value) return t('showLess');
  const plural = hiddenNumbers.value.length === 1 ? '' : 's';
  return t('andOthers', { 0: hiddenNumbers.value.length, 1: plural });
});

function handleError(apiError: ApiError) {
  error.value = apiError.i18nPath;
}

async function resend(event: Event) {
  event.preventDefault();
  await verificationService.resend(verificationId);
  resendTimer.value = 30;
}

async function onSubmit() {
  error.value = null;

  const response = (await verificationService.validate(
    verificationId,
    code.value,
  )) as unknown as Record<string, unknown>;

  if (response?.errorCode) {
    handleError(response as unknown as ApiError);
  } else if (response.verificationCodeRequired === false && response.location) {
    window.location.href = response.location as string;
  }
}

watch(
  resendTimer,
  (value) => {
    if (value > 0) setTimeout(() => resendTimer.value--, 1000);
  },
  { immediate: true },
);

onMounted(async () => {
  const verificationCode = (await verificationService.get(verificationId)) as unknown as Record<
    string,
    unknown
  >;

  if (verificationCode.errorCode) {
    handleError(verificationCode as unknown as ApiError);
  } else {
    sentTo.value = (verificationCode.sentTo as string) ?? '';
  }

  // WebOTP: auto-fill and submit when the browser supports it.
  if (!('OTPCredential' in window)) return;

  const controller = new AbortController();
  formEl.value?.addEventListener('submit', () => controller.abort());

  navigator.credentials
    .get({ otp: { transport: ['sms'] }, signal: controller.signal } as CredentialRequestOptions)
    .then((credential) => {
      const otp = (credential as { code?: string } | null)?.code;
      if (!otp) return;

      code.value = otp;
      void onSubmit();
    })
    .catch((err) => console.log(err));
});
</script>
